import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Button from 'core/modules/atoms/Button';
import { useAnalyticsFilters } from '../../../../contexts/AnalyticsFiltersContext';
import StudentConversationDrawer from '../../../StudentConversationDrawer/StudentConversationDrawer';
import InappropriateUseCard from './InappropriateUseCard/InappropriateUseCard';
import './_inappropriate-use-section.scss';
import type { AnnotationStatus, FlaggedMessage } from './useInappropriateUseSection';
import useInappropriateUseSection from './useInappropriateUseSection';

type InappropriateUseSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

function InappropriateUseSection({ allCourseIds, classValue, groupCourseMap, timePeriodValue }: InappropriateUseSectionProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);
  const history = useHistory();
  const { groups } = useAnalyticsFilters();
  const [selectedMessage, setSelectedMessage] = useState<FlaggedMessage | null>(null);
  const { handleMarkAllAsAttended, markingAll, messages, refetchMessages, removeMessage, total, loading } = useInappropriateUseSection({
    allCourseIds,
    classValue,
    groupCourseMap,
    timePeriodValue,
  });

  const teacherMap = useMemo(() => {
    const map: Record<string, string> = {};
    const schoolGroups = groups?.schoolGroups || [];
    schoolGroups.forEach((group: { users?: Array<{ guid: string; name: string; lastname: string }> }) => {
      (group.users || []).forEach((user) => {
        if (user.guid && !map[user.guid]) {
          map[user.guid] = `${user.name} ${user.lastname}`.trim();
        }
      });
    });
    return map;
  }, [groups]);

  const enrichedMessages = useMemo(() => {
    return messages.map((msg) => {
      if (msg.reviewerName || !msg.annotationReviewedById) return msg;
      const name = teacherMap[msg.annotationReviewedById];
      if (name) return { ...msg, reviewerName: name };
      return msg;
    });
  }, [messages, teacherMap]);

  const handleStatusChanged = useCallback(
    (annotationId: string, newStatus: AnnotationStatus, reviewerName: string) => {
      setSelectedMessage((prev) => {
        if (!prev || prev.annotationId !== annotationId) return prev;
        return { ...prev, annotationStatus: newStatus, reviewerName };
      });
      removeMessage(annotationId);
      refetchMessages();
    },
    [removeMessage, refetchMessages]
  );

  const hasPendingAnnotations = messages.some((msg) => msg.annotationStatus === 'PENDING');

  if (loading) {
    return null;
  }

  return (
    <div className="inappropriate-use-section">
      <div className="inappropriate-use-section__header">
        <div className="inappropriate-use-section__header-titles">
          <h2 className="inappropriate-use-section__title">{t('chatbot:ai_analytics_inappropriate_use_title')}</h2>
          <p className="inappropriate-use-section__subtitle">{t('chatbot:ai_analytics_inappropriate_use_subtitle', { count: total })}</p>
        </div>
        <div className="inappropriate-use-section__header-actions">
          {hasPendingAnnotations && (
            <Button
              color="transparent-primary"
              disabled={markingAll}
              onClick={handleMarkAllAsAttended}
              size="xs"
              text={t('chatbot:ai_analytics_inappropriate_use_mark_all')}
              type="button"
              variant="inline"
            />
          )}
          <Button
            color="primary-outlined"
            onClick={() => history.push(`/analytics/flagged-messages?class=${classValue}&period=${timePeriodValue}`)}
            size="xs"
            text={t('chatbot:ai_analytics_inappropriate_use_view_all', { count: total })}
            type="button"
            variant="outline"
          />
        </div>
      </div>

      {enrichedMessages.length > 0 && (
        <div className="inappropriate-use-section__cards">
          {enrichedMessages.map((message) => (
            <InappropriateUseCard key={message.annotationId} message={message} onClick={setSelectedMessage} />
          ))}
        </div>
      )}
      <StudentConversationDrawer
        message={selectedMessage}
        onClose={() => setSelectedMessage(null)}
        onStatusChanged={handleStatusChanged}
        open={!!selectedMessage}
      />
    </div>
  );
}

export default InappropriateUseSection;
